import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { formatApiError } from '@/lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_inventory-engine-2/artifacts/2d9zezs2_Zippy%20Logo%202.PNG';

export default function Setup({ onSetupComplete }) {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/setup-status`);
        if (!res.data.needs_setup) {
          // Setup already done — bounce to login
          navigate('/login', { replace: true });
          return;
        }
        setEmail(res.data.suggested_email || '');
        setName(res.data.suggested_name || '');
      } catch (e) {
        setErrorMsg(formatApiError(e));
      } finally {
        setChecking(false);
      }
    })();
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (password.length < 8) {
      setErrorMsg('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setErrorMsg('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/setup`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        password,
      });
      toast.success(`Welcome, ${res.data.user.name}! Your Super Admin account is ready.`);
      onSetupComplete(res.data.token, res.data.user);
      navigate('/', { replace: true });
    } catch (err) {
      setErrorMsg(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#1680e5]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1680e5] to-[#00a89c]" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white max-w-md">
            <ShieldCheck className="h-16 w-16 mb-6 text-white/90" />
            <h1 className="text-5xl font-bold mb-4">Set up your Zippy WMS</h1>
            <p className="text-lg text-white/90 mb-8">
              You&apos;re the first person here. Create your Super Admin account and pick a strong password.
            </p>
            <ul className="space-y-3 text-white/90">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <span>Only you know your password — nothing is emailed or copied down.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <span>Once set up, you can invite Admins, Supervisors and Operators from the Users page.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <span>Every action they take is captured in the audit trail, tied to their account.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center mb-8">
            <img src={LOGO_URL} alt="Zippy WMS" className="h-16 w-auto" />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Create Super Admin</CardTitle>
              <CardDescription>
                This is a one-time setup. The next visitor will see the normal login screen.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="setup-name">Full name</Label>
                  <Input
                    id="setup-name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Kwame Agyei-Owusu"
                    autoFocus={!name}
                    data-testid="setup-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="setup-email">Email</Label>
                  <Input
                    id="setup-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid="setup-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="setup-password">Password</Label>
                  <Input
                    id="setup-password"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    autoFocus={!!name}
                    data-testid="setup-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="setup-confirm">Confirm password</Label>
                  <Input
                    id="setup-confirm"
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="setup-confirm-input"
                  />
                </div>
                {errorMsg && (
                  <div
                    className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                    data-testid="setup-error"
                  >
                    {errorMsg}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1680e5] hover:bg-[#1680e5]/90"
                  data-testid="setup-submit-button"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating your account…</>
                  ) : (
                    'Create Super Admin & sign in'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
